import type {
	UploadProgressMessage,
	UploadProgressPhoto,
	UploadProgressSource,
	UploadProgressStage,
} from 'interfaces/app';
import {
	PHOTO_PROCESSING_ERROR_CODES,
	type PhotoProcessingErrorCode,
} from 'interfaces/project';
import {
	getUploadProgressIdentityKey,
	normalizeUploadProgressPercentage,
} from 'services/upload/external-upload-helpers';
import { clamp } from 'utils/number';
import {
	assertEvent, assign, enqueueActions, fromCallback, fromPromise, setup,
} from 'xstate';

export type UploadTransport = 'web' | 'native' | 'external';

export type UploadValidationError = {
	kind: 'validation';
	message: string;
	rule?: string;
	retryable: false;
};

export type UploadUploadError = {
	kind: 'upload';
	message: string;
	retryable: boolean;
};

export type UploadProcessingError = {
	code?: PhotoProcessingErrorCode;
	kind: 'processing';
	message: string;
	retryable: boolean;
};

export type UploadCancelledError = {
	kind: 'cancelled';
	message: string;
	retryable: false;
	/*
	 * The failure the user was reacting to when they cancelled, preserved
	 * for post-mortem diagnosis instead of being clobbered by the generic
	 * cancellation error.
	 */
	cause?: UploadValidationError | UploadUploadError | UploadProcessingError;
};

export type UploadError =
	| UploadValidationError
	| UploadUploadError
	| UploadProcessingError
	| UploadCancelledError;

export type UploadErrorKind = UploadError['kind'];

export type UploadItemInput = {
	uploadId: string;
	name: string;
	totalBytes: number;
	transport: UploadTransport;
	localPhotoId?: string;
	maxRetries?: number;
	uploadedBytes?: number;
	uploadUrl?: string;
};

export type UploadItemContext = {
	uploadId: string;
	name: string;
	totalBytes: number;
	transport: UploadTransport;
	localPhotoId: string | null;
	remotePhotoId: number | null;
	uploadUrl: string | null;
	uploadedBytes: number;
	/*
	 * Total retries performed across both phases (kept for projection and
	 * diagnostics). The per-phase budgets below are what the retry guards
	 * consume: an upload retry must not eat into the processing budget.
	 */
	retryCount: number;
	uploadRetryCount: number;
	processingRetryCount: number;
	maxRetries: number;
	error: UploadError | null;
};

export type UploadItemEvent =
	| { type: 'upload.validate' }
	| { type: 'upload.progress';
		uploadedBytes: number;
		totalBytes: number }
	| { type: 'upload.completed';
		uploadUrl: string }
	| { type: 'upload.failed';
		error: UploadError }
	| { type: 'upload.retry' }
	/*
	 * User-initiated retry that clears the exhausted retry budget so a
	 * count-capped item can attempt again after a transient backend outage
	 * (e.g. a cold process-photo Lambda). Automatic retries use 'upload.retry'
	 * and stay capped so we never hammer a failing backend.
	 */
	| { type: 'upload.retry.reset' }
	| { type: 'upload.cancel' }
	| { type: 'upload.remove' }
	| { type: 'network.offline' }
	| { type: 'network.online' };

export type UploadItemTag = 'pending' | 'active' | 'failed' | 'retryable' | 'save-blocking' | 'terminal';

export type UploadItemTerminationReason = 'saved' | 'rejected' | 'removed' | 'cancelled';

export type UploadItemEmittedEvent =
	| { type: 'item.failed';
		error: UploadError }
	| { type: 'item.processingFailed';
		error: UploadError }
	| { type: 'item.queueSlotReleasable' }
	| { type: 'item.terminated';
		reason: UploadItemTerminationReason };

export type CreateLocalPhotoOutput = {
	localPhotoId: string;
};

export type ProcessUploadedPhotoOutput = {
	remotePhotoId: number;
};

export type UploadItemActorInput = UploadItemContext;

export type UploadBatchItemStatus =
	| 'pending'
	| 'active'
	| 'failed'
	| 'saved'
	| 'rejected'
	| 'cancelled'
	| 'removed';

export type UploadBatchItemSnapshot = {
	id: string;
	status: UploadBatchItemStatus;
	uploadedBytes: number;
	totalBytes: number;
	saveBlocking: boolean;
};

export type UploadBatchContext = {
	items: Record<string, UploadBatchItemSnapshot>;
	online: boolean;
};

export type UploadBatchEvent =
	| { type: 'batch.files.accepted';
		items: UploadBatchItemSnapshot[] }
	| { type: 'batch.reset' }
	| { type: 'item.snapshot';
		item: UploadBatchItemSnapshot }
	| { type: 'item.removed';
		id: string }
	| { type: 'network.online' }
	| { type: 'network.offline' };

export type UploadBatchTag = 'idle' | 'active' | 'offline' | 'failed' | 'save-blocking' | 'ready-to-save';

export type WebUploadQueueContext = {
	activeIds: string[];
	maxActive: number;
	online: boolean;
	pendingIds: string[];
};

export type WebUploadQueueEvent =
	| { type: 'webQueue.upload.accepted';
		id: string }
	| { type: 'webQueue.upload.released';
		id: string }
	| { type: 'webQueue.upload.removed';
		id: string }
	| { type: 'webQueue.reset' }
	| { type: 'network.online' }
	| { type: 'network.offline' };

export type WebUploadQueueInput = {
	maxActive: number;
	online: boolean;
};

export type WebUploadQueueEmittedEvent = {
	type: 'webQueue.upload.activated';
	id: string;
};

export type WebUploadQueueTag = 'online' | 'offline';

export type PhotoProcessingQueueContext = {
	activeIds: string[];
	maxActive: number;
	online: boolean;
	pendingIds: string[];
};

export type PhotoProcessingQueueEvent =
	| { type: 'photoQueue.task.accepted';
		id: string }
	| { type: 'photoQueue.task.released';
		id: string }
	| { type: 'photoQueue.task.removed';
		id: string }
	| { type: 'photoQueue.reset' }
	| { type: 'network.online' }
	| { type: 'network.offline' };

export type PhotoProcessingQueueInput = {
	maxActive: number;
	online: boolean;
};

export type PhotoProcessingQueueEmittedEvent = {
	type: 'photoQueue.task.activated';
	id: string;
};

export type PhotoProcessingQueueTag = 'online' | 'offline';

export type ExternalUploadPhoto = UploadProgressPhoto;

export type ExternalUploadProgress = UploadProgressMessage;

export type ExternalUploadSessionContext = {
	batchId: string | null;
	photos: ExternalUploadPhoto[];
	progressPercentage: number;
	sequence: number;
	source: UploadProgressSource | null;
	stage: UploadProgressStage;
	submittedCount: number;
	supersededBatchIdentities: string[];
	uploadSessionId: string | null;
};

export type ExternalUploadSessionEvent =
	| { type: 'externalUpload.qr.requested' }
	| { type: 'externalUpload.qr.shown' }
	| { type: 'externalUpload.qr.failed' }
	| { type: 'externalUpload.qr.aborted' }
	| { type: 'externalUpload.progress.received';
		data: ExternalUploadProgress }
	| { type: 'externalUpload.editing.started' }
	| { type: 'externalUpload.reset' };

export type ExternalUploadSessionEmittedEvent =
	| { type: 'externalUpload.qr.shouldClose' }
	| { type: 'externalUpload.batch.itemsAccepted';
		items: UploadBatchItemSnapshot[] }
	| { type: 'externalUpload.batch.itemSnapshot';
		item: UploadBatchItemSnapshot };

export type ExternalUploadSessionTag =
	| 'qr-loading'
	| 'qr-visible'
	| 'active'
	| 'ready-to-start-editing'
	| 'terminal';

const defaultMaxRetries = 3;

export function validateUploadInput(input: { totalBytes: number }): void {
	if (input.totalBytes <= 0) {
		throw new Error('Upload total bytes must be greater than zero');
	}
}

/**
 * Returned by upload adapters when the failure is deterministic (missing
 * credentials, missing bucket model, malformed upload result) so the item
 * machine marks the upload as terminally failed instead of burning retry budget.
 */
export class NonRetryableUploadError extends Error {
	public readonly retryable = false;

	public constructor(message: string) {
		super(message);
		this.name = 'NonRetryableUploadError';
	}
}

/**
 * Returned by upload-processing adapters when the failure is deterministic
 * (missing URL, photo-model mismatch, schema error) so the item machine
 * marks the upload as terminally failed rather than re-queuing for retry.
 * Recognized by {@link setThrownFailure}.
 */
export class NonRetryableUploadProcessingError extends Error {
	public readonly retryable = false;

	public constructor(message: string) {
		super(message);
		this.name = 'NonRetryableUploadProcessingError';
	}
}

function toItemsById(items: UploadBatchItemSnapshot[]): Record<string, UploadBatchItemSnapshot> {
	return Object.fromEntries(items.map((item) => [
		item.id,
		item,
	]));
}

function removeItem(
	items: Record<string, UploadBatchItemSnapshot>,
	id: string,
): Record<string, UploadBatchItemSnapshot> {
	const { [id]: ignoredRemoved, ...remainingItems } = items;

	return remainingItems;
}

function removeId(
	ids: string[],
	id: string,
): string[] {
	return ids.filter((currentId) => currentId !== id);
}

/*
 * Shared by both concurrency-queue machines. Their pool semantics live in
 * enqueueConcurrencyPoolId/drainConcurrencyPool below; the two setup()
 * shells stay deliberately duplicated — typed, declarative config beats a
 * generics-heavy factory over template-literal event names, and the
 * symmetric hardening tests pin both copies against drift.
 */
function validateConcurrencyQueueInput(
	queueLabel: string,
	input: { maxActive: number },
): void {
	if (!Number.isInteger(input.maxActive) || input.maxActive <= 0) {
		throw new Error(`${queueLabel} maxActive must be a positive integer`);
	}
}

type ConcurrencyPoolShape = {
	activeIds: string[];
	maxActive: number;
	online: boolean;
	pendingIds: string[];
};

function enqueueConcurrencyPoolId(
	context: ConcurrencyPoolShape,
	id: string,
): string[] {
	/*
	 * A falsy id would be shift()ed out of the pool during drain and
	 * silently vanish (never activated, never re-queued). An empty id is
	 * always a caller bug, so reject it loudly at the boundary.
	 */
	if (!id) {
		throw new Error('Concurrency pool requires a non-empty id');
	}

	if (
		context.activeIds.includes(id)
		|| context.pendingIds.includes(id)
	) {
		return context.pendingIds;
	}

	return [
		...context.pendingIds,
		id,
	];
}

function drainConcurrencyPool(context: ConcurrencyPoolShape): {
	activeIds: string[];
	activatedIds: string[];
	pendingIds: string[];
} {
	const activeIds = [...context.activeIds];
	const pendingIds = [...context.pendingIds];
	const activatedIds: string[] = [];

	if (!context.online) {
		return {
			activeIds,
			activatedIds,
			pendingIds,
		};
	}

	while (
		activeIds.length < context.maxActive
		&& pendingIds.length > 0
	) {
		const uploadId = pendingIds.shift();

		if (!uploadId || activeIds.includes(uploadId)) {
			continue;
		}

		activeIds.push(uploadId);
		activatedIds.push(uploadId);
	}

	return {
		activeIds,
		activatedIds,
		pendingIds,
	};
}

/**
 * Preserve only recognized process-photo domain codes while normalizing an
 * unknown thrown value into the upload state-machine error contract.
 *
 * @param error Error rejected by the photo-processing adapter.
 * @returns A known {@link PhotoProcessingErrorCode}, when one was attached.
 */
function getPhotoProcessingErrorCode(error: unknown): PhotoProcessingErrorCode | undefined {
	if (
		!(error instanceof Error)
		|| !('code' in error)
	) {
		return undefined;
	}

	const code = (error as { code?: unknown }).code;
	const knownCodes: readonly string[] = PHOTO_PROCESSING_ERROR_CODES;

	return (
		typeof code === 'string'
		&& knownCodes.includes(code)
	)
		? code as PhotoProcessingErrorCode
		: undefined;
}

function hasConfiguredRetryBudget(context: UploadItemContext): boolean {
	return context.maxRetries > 0;
}

function isErrorRetryable(context: UploadItemContext): boolean {
	return context.error?.retryable === true;
}

type NormalizedExternalUploadProgress = {
	photos: ExternalUploadPhoto[];
	progressPercentage: number;
	submittedCount: number;
};

function normalizeExternalUploadProgress(progress: ExternalUploadProgress): NormalizedExternalUploadProgress {
	return {
		progressPercentage: normalizeUploadProgressPercentage(progress),
		submittedCount: progress.submittedCount,
		photos: progress.photos ?? [],
	};
}

function isExternalUploadProgressComplete(progress: ExternalUploadProgress): boolean {
	if (progress.version === 2) {
		return progress.stage === 'completed';
	}

	const normalizedProgress = normalizeExternalUploadProgress(progress);

	return (
		normalizedProgress.submittedCount > 0
		&& (
			normalizedProgress.progressPercentage >= 100
			|| normalizedProgress.photos.length >= normalizedProgress.submittedCount
		)
	);
}

export function getExternalUploadItemIds(
	submittedCount: number,
	batchId?: string,
): string[] {
	const idPrefix = batchId
		? `${batchId}:external-upload`
		: 'external-upload';

	return Array.from(
		{
			length: submittedCount,
		},
		(_ignoredValue, index) => `${idPrefix}-${index}`,
	);
}

function toExternalUploadPendingBatchItems(
	submittedCount: number,
	batchId?: string,
): UploadBatchItemSnapshot[] {
	return getExternalUploadItemIds(
		submittedCount,
		batchId,
	).map((id) => ({
		id,
		status: 'pending',
		uploadedBytes: 0,
		totalBytes: 100,
		saveBlocking: true,
	}));
}

function toExternalUploadProgressBatchItems(progress: ExternalUploadProgress): UploadBatchItemSnapshot[] {
	const normalizedProgress = normalizeExternalUploadProgress(progress);
	const terminalStatus: UploadBatchItemStatus | null = progress.version === 2
		? progress.stage === 'completed'
			? 'saved'
			: progress.stage === 'failed'
				? 'failed'
				: progress.stage === 'aborted'
					? 'cancelled'
					: null
		: isExternalUploadProgressComplete(progress)
			? 'saved'
			: null;
	const batchId = progress.version === 2
		? progress.batchId
		: undefined;

	return getExternalUploadItemIds(
		normalizedProgress.submittedCount,
		batchId,
	).map((id) => ({
		id,
		status: terminalStatus ?? 'active',
		uploadedBytes: terminalStatus === 'saved' ? 100 : normalizedProgress.progressPercentage,
		totalBytes: 100,
		saveBlocking: terminalStatus === null,
	}));
}

function uploadBatchItems(context: UploadBatchContext): UploadBatchItemSnapshot[] {
	return Object.values(context.items);
}

function hasItems(context: UploadBatchContext): boolean {
	return uploadBatchItems(context).length > 0;
}

function hasSaveBlockingItems(context: UploadBatchContext): boolean {
	return uploadBatchItems(context).some((item) => item.saveBlocking);
}

function hasFailedSaveBlockingItems(context: UploadBatchContext): boolean {
	return uploadBatchItems(context).some((item) => item.status === 'failed' && item.saveBlocking);
}

function hasActiveItems(context: UploadBatchContext): boolean {
	return uploadBatchItems(context).some((item) => item.status === 'pending' || item.status === 'active');
}

function isReadyToSave(context: UploadBatchContext): boolean {
	return hasItems(context) && !hasSaveBlockingItems(context) && !hasActiveItems(context);
}

export function getUploadItemProgress(context: UploadItemContext): number {
	if (context.totalBytes <= 0) {
		return 0;
	}

	return Math.round((context.uploadedBytes / context.totalBytes) * 100);
}

export function getUploadBatchProgress(context: UploadBatchContext): number {
	const totals = uploadBatchItems(context).reduce(
		(accumulator, item) => ({
			uploadedBytes: accumulator.uploadedBytes + item.uploadedBytes,
			totalBytes: accumulator.totalBytes + item.totalBytes,
		}),
		{
			uploadedBytes: 0,
			totalBytes: 0,
		},
	);

	if (totals.totalBytes <= 0) {
		return 0;
	}

	return Math.round((totals.uploadedBytes / totals.totalBytes) * 100);
}

export const uploadItemMachine = setup({
	types: {} as {
		context: UploadItemContext;
		events: UploadItemEvent;
		input: UploadItemInput;
		tags: UploadItemTag;
		emitted: UploadItemEmittedEvent;
	},
	actors: {
		validateUpload: fromPromise(async ({ input }: { input: UploadItemActorInput }): Promise<void> => {
			validateUploadInput(input);
		}),
		createLocalPhoto: fromPromise(
			async ({ input }: { input: UploadItemActorInput }): Promise<CreateLocalPhotoOutput> => ({
				localPhotoId: input.localPhotoId ?? input.uploadId,
			}),
		),
		uploadFile: fromCallback<UploadItemEvent, UploadItemActorInput>(() => {
			return () => {};
		}),
		processUploadedPhoto: fromPromise(
			async ({ input: _ignoredInput }: { input: UploadItemActorInput }): Promise<ProcessUploadedPhotoOutput> => {
				/*
				 * Non-retryable: a missing actor is a wiring bug that retries
				 * can never fix; burning the retry budget on it only delays
				 * the (deterministic) processingFailed state.
				 */
				throw new NonRetryableUploadProcessingError('Missing upload photo processing actor');
			},
		),
	},
	actions: {
		setLocalPhotoId: assign({
			localPhotoId: (_actorScope, params: CreateLocalPhotoOutput) => params.localPhotoId,
		}),
		setUploadProgress: assign({
			uploadedBytes: (_actorScope, params: { uploadedBytes: number;
				totalBytes: number }) => (
				clamp(
					params.uploadedBytes,
					0,
					Math.max(
						0,
						params.totalBytes,
					),
				)
			),
			totalBytes: (_actorScope, params: { uploadedBytes: number;
				totalBytes: number }) => (
				Math.max(
					0,
					params.totalBytes,
				)
			),
		}),
		setUploadUrl: assign({
			uploadUrl: (_actorScope, params: { uploadUrl: string }) => params.uploadUrl,
			uploadedBytes: ({ context }) => context.totalBytes,
			error: () => null,
		}),
		setUploadError: assign({
			error: (_actorScope, params: { error: UploadError }) => params.error,
		}),
		setThrownFailure: assign({
			error: (
				_actorScope,
				params: { error: unknown;
					kind: 'validation' | 'processing' },
			): UploadError => {
				const message = params.error instanceof Error
					? params.error.message
					: String(params.error);

				if (params.kind === 'validation') {
					return {
						kind: 'validation',
						message,
						retryable: false,
					};
				}

				/*
				 * Processing errors default to retryable=true (transient server
				 * hiccups, etc.). Callers that throw a deterministic invariant
				 * failure (missing url, photo-model mismatch, etc.) attach
				 * `retryable: false` to the Error instance so we honor that.
				 */
				const explicitRetryable = (
					params.error instanceof Error
					&& 'retryable' in params.error
					&& typeof (params.error as { retryable?: unknown }).retryable === 'boolean'
				)
					? (params.error as { retryable: boolean }).retryable
					: true;
				const code = getPhotoProcessingErrorCode(params.error);

				return {
					...(
						code
							? {
								code,
							}
							: {}
					),
					kind: 'processing',
					message,
					retryable: explicitRetryable,
				};
			},
		}),
		clearError: assign({
			error: () => null,
		}),
		incrementUploadRetry: assign({
			retryCount: ({ context }) => context.retryCount + 1,
			uploadRetryCount: ({ context }) => context.uploadRetryCount + 1,
			uploadedBytes: () => 0,
			error: () => null,
		}),
		incrementProcessingRetry: assign({
			retryCount: ({ context }) => context.retryCount + 1,
			processingRetryCount: ({ context }) => context.processingRetryCount + 1,
			error: () => null,
		}),
		/*
		 * Reset the retry budgets for a user-initiated retry. The 'upload.retry.reset'
		 * transitions are guarded by canResetRetry, so non-retryable failures or
		 * items configured with no retry budget never reset and .can('upload.retry.reset')
		 * stays false. `error` is left intact so the canRetryUpload/canRetryProcessing
		 * guards keep gating on retryable.
		 */
		resetRetries: assign({
			retryCount: () => 0,
			uploadRetryCount: () => 0,
			processingRetryCount: () => 0,
		}),
		setRemotePhotoId: assign({
			remotePhotoId: (_actorScope, params: ProcessUploadedPhotoOutput) => params.remotePhotoId,
			error: () => null,
		}),
		setCancelled: assign({
			error: ({ context }): UploadError => ({
				kind: 'cancelled',
				message: 'Upload cancelled',
				retryable: false,
				...(context.error && context.error.kind !== 'cancelled'
					? {
						cause: context.error,
					}
					: {}),
			}),
		}),
		emitFailed: enqueueActions(({ context, enqueue }) => {
			if (context.error === null) {
				/*
				 * Failed states are only reachable with an error set; emitting
				 * a failure without diagnostic payload is an internal bug.
				 */
				throw new Error('item.failed emitted without an error in context');
			}

			enqueue.emit({
				type: 'item.failed',
				error: context.error,
			});
		}),
		emitProcessingFailed: enqueueActions(({ context, enqueue }) => {
			if (context.error === null) {
				throw new Error('item.processingFailed emitted without an error in context');
			}

			enqueue.emit({
				type: 'item.processingFailed',
				error: context.error,
			});
		}),
		emitQueueSlotReleasable: enqueueActions(({ enqueue }) => {
			enqueue.emit({
				type: 'item.queueSlotReleasable',
			});
		}),
		emitTerminated: enqueueActions((
			{ enqueue },
			params: { reason: UploadItemTerminationReason },
		) => {
			enqueue.emit({
				type: 'item.terminated',
				reason: params.reason,
			});
		}),
	},
	guards: {
		canResetRetry: ({ context }) => (
			isErrorRetryable(context)
			&& hasConfiguredRetryBudget(context)
		),
		canRetryUpload: ({ context }) => (
			isErrorRetryable(context) && context.uploadRetryCount < context.maxRetries
		),
		canRetryProcessing: ({ context }) => (
			isErrorRetryable(context) && context.processingRetryCount < context.maxRetries
		),
		/*
		 * Only web uploads are safe to pause on offline: re-entering 'uploading'
		 * re-invokes the AWS uploader and restarts the transfer. Native uploads
		 * are driven by the native bridge — pausing would dispose the lifecycle
		 * handle (dropping later onUploadSucceeded/onUploadFailed bridge events)
		 * and resuming would not re-trigger webToNative.retryUpload, wedging the
		 * item forever. Native connectivity is the native layer's concern.
		 */
		isWebTransport: ({ context }) => context.transport === 'web',
		hasCompletedReconciledUpload: ({ context }) => (
			context.uploadUrl !== null
			&& context.uploadedBytes === context.totalBytes
		),
	},
}).createMachine({
	id: 'uploadItem',
	context: ({ input }) => ({
		uploadId: input.uploadId,
		name: input.name,
		totalBytes: input.totalBytes,
		transport: input.transport,
		localPhotoId: input.localPhotoId ?? null,
		remotePhotoId: null,
		uploadUrl: input.uploadUrl ?? null,
		uploadedBytes: clamp(
			input.uploadedBytes ?? 0,
			0,
			input.totalBytes,
		),
		retryCount: 0,
		uploadRetryCount: 0,
		processingRetryCount: 0,
		maxRetries: input.maxRetries ?? defaultMaxRetries,
		error: null,
	}),
	initial: 'selected',
	states: {
		selected: {
			tags: [
				'pending',
				'save-blocking',
			],
			on: {
				'upload.validate': {
					target: 'validating',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		validating: {
			tags: [
				'active',
				'save-blocking',
			],
			invoke: {
				src: 'validateUpload',
				input: ({ context }) => context,
				onDone: {
					target: 'creatingLocalPhoto',
					actions: 'clearError',
				},
				onError: {
					target: 'rejected',
					actions: {
						type: 'setThrownFailure',
						params: ({ event }) => ({
							error: event.error,
							kind: 'validation',
						}),
					},
				},
			},
			on: {
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		creatingLocalPhoto: {
			tags: [
				'active',
				'save-blocking',
			],
			invoke: {
				src: 'createLocalPhoto',
				input: ({ context }) => context,
				onDone: {
					target: 'queuedForUpload',
					actions: {
						type: 'setLocalPhotoId',
						params: ({ event }) => event.output,
					},
				},
				/*
				 * Local-photo creation failures are deliberately terminal
				 * (kind 'validation', non-retryable): the adapter only fails
				 * on malformed input/state, not transient I/O, so a retry
				 * path would re-run a deterministic failure.
				 */
				onError: {
					target: 'rejected',
					actions: {
						type: 'setThrownFailure',
						params: ({ event }) => ({
							error: event.error,
							kind: 'validation',
						}),
					},
				},
			},
			on: {
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		queuedForUpload: {
			tags: [
				'pending',
				'save-blocking',
			],
			always: [
				{
					guard: 'hasCompletedReconciledUpload',
					target: 'uploaded',
				},
				{
					target: 'uploading',
				},
			],
		},
		uploading: {
			tags: [
				'active',
				'save-blocking',
			],
			invoke: {
				src: 'uploadFile',
				input: ({ context }) => context,
			},
			on: {
				'upload.progress': {
					actions: {
						type: 'setUploadProgress',
						params: ({ event }) => ({
							uploadedBytes: event.uploadedBytes,
							totalBytes: event.totalBytes,
						}),
					},
				},
				'upload.completed': {
					target: 'uploaded',
					actions: {
						type: 'setUploadUrl',
						params: ({ event }) => ({
							uploadUrl: event.uploadUrl,
						}),
					},
				},
				'upload.failed': {
					target: 'uploadFailed',
					actions: {
						type: 'setUploadError',
						params: ({ event }) => ({
							error: event.error,
						}),
					},
				},
				/*
				 * Symmetric with processingPhoto's offline pause: a drop mid-upload
				 * can orphan the in-flight transfer (the adapter neither completes
				 * nor fails), leaving the item wedged here. Leaving 'uploading'
				 * stops the invoked uploadFile actor, which aborts the transfer via
				 * its lifecycle signal; reconnect re-invokes a fresh upload. Guarded
				 * to web only — see isWebTransport for why native must not pause.
				 */
				'network.offline': {
					guard: 'isWebTransport',
					target: 'uploadOffline',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		uploadOffline: {
			tags: [
				'active',
				'save-blocking',
			],
			on: {
				'network.online': {
					target: 'uploading',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		uploadFailed: {
			tags: [
				'failed',
				'retryable',
				'save-blocking',
			],
			entry: [
				'emitFailed',
				'emitQueueSlotReleasable',
			],
			on: {
				'upload.retry': {
					guard: 'canRetryUpload',
					target: 'queuedForUpload',
					actions: 'incrementUploadRetry',
				},
				'upload.retry.reset': {
					guard: 'canResetRetry',
					actions: 'resetRetries',
				},
				'upload.remove': {
					target: 'removed',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
			},
		},
		uploaded: {
			tags: [
				'active',
				'save-blocking',
			],
			always: {
				target: 'processingPhoto',
			},
		},
		processingPhoto: {
			tags: [
				'active',
				'save-blocking',
			],
			entry: 'emitQueueSlotReleasable',
			invoke: {
				src: 'processUploadedPhoto',
				input: ({ context }) => context,
				onDone: {
					target: 'saved',
					actions: {
						type: 'setRemotePhotoId',
						params: ({ event }) => event.output,
					},
				},
				onError: {
					target: 'processingFailed',
					actions: {
						type: 'setThrownFailure',
						params: ({ event }) => ({
							error: event.error,
							kind: 'processing',
						}),
					},
				},
			},
			on: {
				/*
				 * The processing request (POST /api/photo) is unguarded by any
				 * concurrency queue, so a connectivity drop mid-flight would
				 * otherwise orphan the in-flight promise and wedge the item here
				 * forever — the batch stays save-blocking and the user can never
				 * save. Pausing abandons the orphaned invoke and resumes with a
				 * fresh request on reconnect, mirroring how the batch/queue
				 * machines model offline as a first-class paused state.
				 */
				'network.offline': {
					target: 'processingOffline',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		processingOffline: {
			tags: [
				'active',
				'save-blocking',
			],
			on: {
				'network.online': {
					target: 'processingPhoto',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
				'upload.remove': {
					target: 'removed',
				},
			},
		},
		processingFailed: {
			tags: [
				'failed',
				'retryable',
				'save-blocking',
			],
			entry: 'emitProcessingFailed',
			on: {
				'upload.retry': {
					guard: 'canRetryProcessing',
					target: 'processingPhoto',
					actions: 'incrementProcessingRetry',
				},
				'upload.retry.reset': {
					guard: 'canResetRetry',
					actions: 'resetRetries',
				},
				'upload.remove': {
					target: 'removed',
				},
				'upload.cancel': {
					target: 'cancelled',
					actions: 'setCancelled',
				},
			},
		},
		rejected: {
			tags: [
				'failed',
				'terminal',
			],
			entry: [
				'emitFailed',
				{
					type: 'emitTerminated',
					params: {
						reason: 'rejected',
					},
				},
			],
			type: 'final',
		},
		saved: {
			tags: ['terminal'],
			entry: {
				type: 'emitTerminated',
				params: {
					reason: 'saved',
				},
			},
			type: 'final',
		},
		cancelled: {
			tags: ['terminal'],
			entry: {
				type: 'emitTerminated',
				params: {
					reason: 'cancelled',
				},
			},
			type: 'final',
		},
		removed: {
			tags: ['terminal'],
			entry: {
				type: 'emitTerminated',
				params: {
					reason: 'removed',
				},
			},
			type: 'final',
		},
	},
});

export const uploadBatchMachine = setup({
	types: {} as {
		context: UploadBatchContext;
		events: UploadBatchEvent;
		tags: UploadBatchTag;
	},
	actions: {
		setAcceptedItems: assign({
			items: ({ event }) => {
				assertEvent(
					event,
					'batch.files.accepted',
				);

				return toItemsById(event.items);
			},
		}),
		mergeAcceptedItems: assign({
			items: ({ context, event }) => {
				assertEvent(
					event,
					'batch.files.accepted',
				);

				return {
					...context.items,
					...toItemsById(event.items),
				};
			},
		}),
		upsertSnapshotItem: assign({
			items: ({ context, event }) => {
				assertEvent(
					event,
					'item.snapshot',
				);

				return {
					...context.items,
					[event.item.id]: event.item,
				};
			},
		}),
		removeEventItem: assign({
			items: ({ context, event }) => {
				assertEvent(
					event,
					'item.removed',
				);

				return removeItem(
					context.items,
					event.id,
				);
			},
		}),
		setOnline: assign({
			online: () => true,
		}),
		setOffline: assign({
			online: () => false,
		}),
		clearItems: assign({
			items: () => ({}),
		}),
	},
	guards: {
		hasFailedSaveBlockingItems: ({ context }) => hasFailedSaveBlockingItems(context),
		hasNoItems: ({ context }) => !hasItems(context),
		isOffline: ({ context }) => !context.online,
		isReadyToSave: ({ context }) => isReadyToSave(context),
	},
}).createMachine({
	id: 'uploadBatch',
	context: {
		items: {},
		online: true,
	},
	initial: 'idle',
	on: {
		'batch.reset': {
			target: '.idle',
			actions: 'clearItems',
		},
		'network.offline': {
			actions: 'setOffline',
		},
		'network.online': {
			actions: 'setOnline',
		},
	},
	states: {
		idle: {
			tags: ['idle'],
			on: {
				'batch.files.accepted': [
					{
						guard: 'isOffline',
						target: 'pausedOffline',
						actions: 'setAcceptedItems',
					},
					{
						target: 'running',
						actions: 'setAcceptedItems',
					},
				],
			},
		},
		running: {
			tags: [
				'active',
				'save-blocking',
			],
			always: [
				{
					guard: 'hasNoItems',
					target: 'idle',
				},
				{
					guard: 'hasFailedSaveBlockingItems',
					target: 'blockedByFailures',
				},
				{
					guard: 'isReadyToSave',
					target: 'readyToSave',
				},
			],
			on: {
				'item.snapshot': {
					actions: 'upsertSnapshotItem',
				},
				'batch.files.accepted': {
					actions: 'mergeAcceptedItems',
				},
				'item.removed': {
					actions: 'removeEventItem',
				},
				'network.offline': {
					target: 'pausedOffline',
					actions: 'setOffline',
				},
			},
		},
		pausedOffline: {
			tags: [
				'offline',
				'save-blocking',
			],
			/*
			 * Items can still mutate while offline (removals, late
			 * snapshots). An emptied batch must declassify immediately:
			 * without this, removing the last item offline leaves an empty
			 * batch advertised as save-blocking until reconnect.
			 */
			always: [
				{
					guard: 'hasNoItems',
					target: 'idle',
				},
			],
			on: {
				'network.online': {
					target: 'running',
					actions: 'setOnline',
				},
				'item.snapshot': {
					actions: 'upsertSnapshotItem',
				},
				'batch.files.accepted': {
					actions: 'mergeAcceptedItems',
				},
				'item.removed': {
					actions: 'removeEventItem',
				},
			},
		},
		blockedByFailures: {
			tags: [
				'failed',
				'save-blocking',
			],
			always: [
				{
					guard: 'hasNoItems',
					target: 'idle',
				},
				{
					guard: 'isReadyToSave',
					target: 'readyToSave',
				},
			],
			on: {
				/*
				 * Reclassify through 'running' (mirrors readyToSave): a failed
				 * item flipping back to 'active' on retry must clear the
				 * batch-level 'failed' state; running's always[] re-derives
				 * the correct classification, bouncing straight back here
				 * when failures genuinely remain.
				 */
				'item.snapshot': {
					target: 'running',
					actions: 'upsertSnapshotItem',
				},
				'batch.files.accepted': {
					target: 'running',
					actions: 'mergeAcceptedItems',
				},
				'item.removed': {
					target: 'running',
					actions: 'removeEventItem',
				},
				'network.offline': {
					target: 'pausedOffline',
					actions: 'setOffline',
				},
			},
		},
		readyToSave: {
			tags: ['ready-to-save'],
			on: {
				'item.snapshot': {
					target: 'running',
					actions: 'upsertSnapshotItem',
				},
				'batch.files.accepted': {
					target: 'running',
					actions: 'mergeAcceptedItems',
				},
				'item.removed': {
					target: 'running',
					actions: 'removeEventItem',
				},
				'network.offline': {
					target: 'pausedOffline',
					actions: 'setOffline',
				},
			},
		},
	},
});

export const webUploadQueueMachine = setup({
	types: {} as {
		context: WebUploadQueueContext;
		emitted: WebUploadQueueEmittedEvent;
		events: WebUploadQueueEvent;
		input: WebUploadQueueInput;
		tags: WebUploadQueueTag;
	},
	actions: {
		setOnline: assign({
			online: () => true,
		}),
		setOffline: assign({
			online: () => false,
		}),
		enqueueUpload: assign({
			pendingIds: ({ context, event }) => {
				assertEvent(
					event,
					'webQueue.upload.accepted',
				);

				return enqueueConcurrencyPoolId(
					context,
					event.id,
				);
			},
		}),
		releaseUpload: assign({
			activeIds: ({ context, event }) => {
				assertEvent(
					event,
					'webQueue.upload.released',
				);

				return removeId(
					context.activeIds,
					event.id,
				);
			},
		}),
		removeUpload: assign({
			activeIds: ({ context, event }) => {
				assertEvent(
					event,
					'webQueue.upload.removed',
				);

				return removeId(
					context.activeIds,
					event.id,
				);
			},
			pendingIds: ({ context, event }) => {
				assertEvent(
					event,
					'webQueue.upload.removed',
				);

				return removeId(
					context.pendingIds,
					event.id,
				);
			},
		}),
		clearQueue: assign({
			activeIds: () => [],
			pendingIds: () => [],
		}),
		activateQueuedUploads: enqueueActions(({ context, enqueue }) => {
			const {
				activeIds,
				activatedIds,
				pendingIds,
			} = drainConcurrencyPool(context);

			enqueue.assign({
				activeIds: () => activeIds,
				pendingIds: () => pendingIds,
			});

			activatedIds.forEach((id) => {
				enqueue.emit({
					type: 'webQueue.upload.activated',
					id,
				});
			});
		}),
	},
	guards: {
		isOffline: ({ context }) => !context.online,
	},
}).createMachine({
	id: 'webUploadQueue',
	context: ({ input }) => {
		validateConcurrencyQueueInput(
			'Web upload queue',
			input,
		);

		return {
			activeIds: [],
			maxActive: input.maxActive,
			online: input.online,
			pendingIds: [],
		};
	},
	initial: 'routing',
	on: {
		'webQueue.reset': {
			target: '.routing',
			actions: 'clearQueue',
		},
	},
	states: {
		routing: {
			always: [
				{
					guard: 'isOffline',
					target: 'offline',
				},
				{
					target: 'online',
				},
			],
		},
		online: {
			tags: ['online'],
			on: {
				'webQueue.upload.accepted': {
					actions: [
						'enqueueUpload',
						'activateQueuedUploads',
					],
				},
				'webQueue.upload.released': {
					actions: [
						'releaseUpload',
						'activateQueuedUploads',
					],
				},
				'webQueue.upload.removed': {
					actions: [
						'removeUpload',
						'activateQueuedUploads',
					],
				},
				'network.offline': {
					target: 'offline',
					actions: 'setOffline',
				},
			},
		},
		offline: {
			tags: ['offline'],
			on: {
				'webQueue.upload.accepted': {
					actions: 'enqueueUpload',
				},
				'webQueue.upload.released': {
					actions: 'releaseUpload',
				},
				'webQueue.upload.removed': {
					actions: 'removeUpload',
				},
				'network.online': {
					target: 'online',
					actions: [
						'setOnline',
						'activateQueuedUploads',
					],
				},
			},
		},
	},
});

export const photoProcessingQueueMachine = setup({
	types: {} as {
		context: PhotoProcessingQueueContext;
		emitted: PhotoProcessingQueueEmittedEvent;
		events: PhotoProcessingQueueEvent;
		input: PhotoProcessingQueueInput;
		tags: PhotoProcessingQueueTag;
	},
	actions: {
		setOnline: assign({
			online: () => true,
		}),
		setOffline: assign({
			online: () => false,
		}),
		enqueueTask: assign({
			pendingIds: ({ context, event }) => {
				assertEvent(
					event,
					'photoQueue.task.accepted',
				);

				return enqueueConcurrencyPoolId(
					context,
					event.id,
				);
			},
		}),
		releaseTask: assign({
			activeIds: ({ context, event }) => {
				assertEvent(
					event,
					'photoQueue.task.released',
				);

				return removeId(
					context.activeIds,
					event.id,
				);
			},
		}),
		removeTask: assign({
			activeIds: ({ context, event }) => {
				assertEvent(
					event,
					'photoQueue.task.removed',
				);

				return removeId(
					context.activeIds,
					event.id,
				);
			},
			pendingIds: ({ context, event }) => {
				assertEvent(
					event,
					'photoQueue.task.removed',
				);

				return removeId(
					context.pendingIds,
					event.id,
				);
			},
		}),
		clearQueue: assign({
			activeIds: () => [],
			pendingIds: () => [],
		}),
		activateQueuedTasks: enqueueActions(({ context, enqueue }) => {
			const {
				activeIds,
				activatedIds,
				pendingIds,
			} = drainConcurrencyPool(context);

			enqueue.assign({
				activeIds: () => activeIds,
				pendingIds: () => pendingIds,
			});

			activatedIds.forEach((id) => {
				enqueue.emit({
					type: 'photoQueue.task.activated',
					id,
				});
			});
		}),
	},
	guards: {
		isOffline: ({ context }) => !context.online,
	},
}).createMachine({
	id: 'photoProcessingQueue',
	context: ({ input }) => {
		validateConcurrencyQueueInput(
			'Photo processing queue',
			input,
		);

		return {
			activeIds: [],
			maxActive: input.maxActive,
			online: input.online,
			pendingIds: [],
		};
	},
	initial: 'routing',
	on: {
		'photoQueue.reset': {
			target: '.routing',
			actions: 'clearQueue',
		},
	},
	states: {
		routing: {
			always: [
				{
					guard: 'isOffline',
					target: 'offline',
				},
				{
					target: 'online',
				},
			],
		},
		online: {
			tags: ['online'],
			on: {
				'photoQueue.task.accepted': {
					actions: [
						'enqueueTask',
						'activateQueuedTasks',
					],
				},
				'photoQueue.task.released': {
					actions: [
						'releaseTask',
						'activateQueuedTasks',
					],
				},
				'photoQueue.task.removed': {
					actions: [
						'removeTask',
						'activateQueuedTasks',
					],
				},
				'network.offline': {
					target: 'offline',
					actions: 'setOffline',
				},
			},
		},
		offline: {
			tags: ['offline'],
			on: {
				'photoQueue.task.accepted': {
					actions: 'enqueueTask',
				},
				'photoQueue.task.released': {
					actions: 'releaseTask',
				},
				'photoQueue.task.removed': {
					actions: 'removeTask',
				},
				'network.online': {
					target: 'online',
					actions: [
						'setOnline',
						'activateQueuedTasks',
					],
				},
			},
		},
	},
});

const MAX_SUPERSEDED_UPLOAD_BATCHES = 32;

/**
 * Retains the current correlated batch as superseded before a new boundary replaces it.
 * The bounded list prevents delayed at-least-once AppSync delivery from reviving old
 * placeholders without allowing a long-lived editor tab to grow context indefinitely.
 *
 * @param context - Current external upload session context.
 * @returns The existing or updated bounded superseded-identity list.
 */
function rememberCurrentUploadBatch(context: ExternalUploadSessionContext): string[] {
	if (!context.uploadSessionId || !context.batchId) {
		return context.supersededBatchIdentities;
	}

	const identity = getUploadProgressIdentityKey(
		context.uploadSessionId,
		context.batchId,
	);

	if (context.supersededBatchIdentities.includes(identity)) {
		return context.supersededBatchIdentities;
	}

	return [
		...context.supersededBatchIdentities,
		identity,
	].slice(-MAX_SUPERSEDED_UPLOAD_BATCHES);
}

export const externalUploadSessionMachine = setup({
	types: {} as {
		context: ExternalUploadSessionContext;
		emitted: ExternalUploadSessionEmittedEvent;
		events: ExternalUploadSessionEvent;
		tags: ExternalUploadSessionTag;
	},
	actions: {
		clearSession: assign({
			batchId: () => null,
			photos: () => [],
			progressPercentage: () => 0,
			sequence: () => -1,
			source: () => null,
			stage: (): UploadProgressStage => 'selected',
			submittedCount: () => 0,
			supersededBatchIdentities: ({ context }) => rememberCurrentUploadBatch(context),
			uploadSessionId: () => null,
		}),
		setProgress: assign({
			batchId: ({ context, event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return event.data.version === 2
					? event.data.batchId
					: context.batchId;
			},
			photos: ({ event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return event.data.photos ?? [];
			},
			progressPercentage: ({ event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return normalizeUploadProgressPercentage(event.data);
			},
			sequence: ({ context, event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return event.data.version === 2
					? event.data.sequence
					: context.sequence;
			},
			source: ({ context, event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return event.data.version === 2
					? event.data.source
					: context.source;
			},
			stage: ({ event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				if (event.data.version === 2) {
					return event.data.stage;
				}

				return isExternalUploadProgressComplete(event.data)
					? 'completed'
					: 'transferring';
			},
			submittedCount: ({ event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return event.data.submittedCount;
			},
			supersededBatchIdentities: ({ context, event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				if (
					event.data.version !== 2
					|| !context.uploadSessionId
					|| !context.batchId
					|| (
						event.data.uploadSessionId === context.uploadSessionId
						&& event.data.batchId === context.batchId
					)
				) {
					return context.supersededBatchIdentities;
				}

				return rememberCurrentUploadBatch(context);
			},
			uploadSessionId: ({ context, event }) => {
				assertEvent(
					event,
					'externalUpload.progress.received',
				);

				return event.data.version === 2
					? event.data.uploadSessionId
					: context.uploadSessionId;
			},
		}),
		closeQrDialog: enqueueActions(({ enqueue }) => {
			enqueue.emit({
				type: 'externalUpload.qr.shouldClose',
			});
		}),
		syncBatchProgress: enqueueActions(({ context, event, enqueue }) => {
			assertEvent(
				event,
				'externalUpload.progress.received',
			);

			if (!event.data.submittedCount) {
				return;
			}

			const incomingBatchId = event.data.version === 2
				? event.data.batchId
				: undefined;

			if (
				context.submittedCount !== event.data.submittedCount
				|| (
					incomingBatchId
					&& incomingBatchId !== context.batchId
				)
			) {
				enqueue.emit({
					type: 'externalUpload.batch.itemsAccepted',
					items: toExternalUploadPendingBatchItems(
						event.data.submittedCount,
						incomingBatchId,
					),
				});
			}

			toExternalUploadProgressBatchItems(event.data).forEach((item) => {
				enqueue.emit({
					type: 'externalUpload.batch.itemSnapshot',
					item,
				});
			});
		}),
	},
	guards: {
		isExternalUploadAborted: ({ event }) => {
			assertEvent(
				event,
				'externalUpload.progress.received',
			);

			return event.data.version === 2 && event.data.stage === 'aborted';
		},
		isExternalUploadComplete: ({ event }) => {
			assertEvent(
				event,
				'externalUpload.progress.received',
			);

			return isExternalUploadProgressComplete(event.data);
		},
		isExternalUploadFailed: ({ event }) => {
			assertEvent(
				event,
				'externalUpload.progress.received',
			);

			return event.data.version === 2 && event.data.stage === 'failed';
		},
	},
}).createMachine({
	id: 'externalUploadSession',
	context: {
		batchId: null,
		photos: [],
		progressPercentage: 0,
		sequence: -1,
		source: null,
		stage: 'selected',
		submittedCount: 0,
		supersededBatchIdentities: [],
		uploadSessionId: null,
	},
	initial: 'idle',
	on: {
		'externalUpload.qr.requested': {
			target: '.loadingQr',
			actions: 'clearSession',
		},
		'externalUpload.reset': {
			target: '.idle',
			actions: 'clearSession',
		},
	},
	states: {
		idle: {
			on: {
				'externalUpload.progress.received': [
					{
						guard: 'isExternalUploadAborted',
						target: 'aborted',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadFailed',
						target: 'failed',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadComplete',
						target: 'readyToStartEditing',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						target: 'receiving',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
				],
			},
		},
		loadingQr: {
			tags: ['qr-loading'],
			on: {
				'externalUpload.qr.shown': {
					target: 'qrVisible',
				},
				'externalUpload.qr.failed': {
					target: 'idle',
				},
				/*
				 * Progress can arrive before the QR ever renders. Emitting
				 * closeQrDialog here (as the qrVisible branches do) tells the
				 * host to tear down the in-flight QR UI; the later qr.shown
				 * event is dropped by receiving/readyToStartEditing.
				 */
				'externalUpload.progress.received': [
					{
						guard: 'isExternalUploadAborted',
						target: 'aborted',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadFailed',
						target: 'failed',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadComplete',
						target: 'readyToStartEditing',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						target: 'receiving',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
				],
			},
		},
		qrVisible: {
			tags: ['qr-visible'],
			on: {
				/*
				 * Abort emits closeQrDialog like every other exit from
				 * qrVisible so programmatic aborts (reset, teardown) cannot
				 * strand an open QR dialog. User-initiated aborts close it
				 * again idempotently.
				 */
				'externalUpload.qr.aborted': {
					target: 'aborted',
					actions: 'closeQrDialog',
				},
				'externalUpload.progress.received': [
					{
						guard: 'isExternalUploadAborted',
						target: 'aborted',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadFailed',
						target: 'failed',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadComplete',
						target: 'readyToStartEditing',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						target: 'receiving',
						actions: [
							'closeQrDialog',
							'syncBatchProgress',
							'setProgress',
						],
					},
				],
			},
		},
		receiving: {
			tags: ['active'],
			on: {
				'externalUpload.progress.received': [
					{
						guard: 'isExternalUploadAborted',
						target: 'aborted',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadFailed',
						target: 'failed',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadComplete',
						target: 'readyToStartEditing',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
				],
			},
		},
		readyToStartEditing: {
			tags: ['ready-to-start-editing'],
			on: {
				'externalUpload.editing.started': {
					target: 'completed',
				},
				/*
				 * A regressing poll (peer submits more photos, progress drops
				 * below complete) re-enters receiving instead of latching
				 * ready-to-start-editing while the batch is save-blocking
				 * again.
				 */
				'externalUpload.progress.received': [
					{
						guard: 'isExternalUploadAborted',
						target: 'aborted',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadFailed',
						target: 'failed',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						guard: 'isExternalUploadComplete',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
					{
						target: 'receiving',
						actions: [
							'syncBatchProgress',
							'setProgress',
						],
					},
				],
			},
		},
		completed: {
			tags: ['terminal'],
		},
		failed: {
			tags: ['terminal'],
		},
		aborted: {
			tags: ['terminal'],
		},
	},
});
